import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import Sidebar from './components/layout/Sidebar';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import OddsBoard from './components/odds/OddsBoard';
import GolfBoard from './components/odds/GolfBoard';
import GameDetail from './components/game/GameDetail';
import ValueAlerts from './components/alerts/ValueAlerts';
import DailyBriefing from './components/briefing/DailyBriefing';
import PatternFinder from './components/patterns/PatternFinder';
import LearnPage from './components/learn/LearnPage';
import ParlayBuilder from './components/parlay/ParlayBuilder';
import BetTracker from './components/bets/BetTracker';
import PredictionsPage from './components/predictions/PredictionsPage';
import PropPicks from './components/props/PropPicks';
import QuotaDashboard from './components/admin/QuotaDashboard';
import AccuracyDashboard from './components/admin/AccuracyDashboard';
import ManualControls from './components/admin/ManualControls';
import AuthPage from './components/auth/AuthPage';
import SettingsPage from './components/settings/SettingsPage';
import PricingPage from './components/pricing/PricingPage';
import AuthProvider, { useAuth } from './contexts/AuthContext';
import AdminGate from './components/admin/AdminGate';

const SPORT_KEYS = [
  'americanfootball_nfl', 'americanfootball_ncaaf', 'basketball_nba',
  'basketball_ncaab', 'baseball_mlb', 'icehockey_nhl', 'soccer_usa_mls',
  'mma_mixed_martial_arts', 'boxing_boxing', 'golf',
];

function AppShell() {
  const { profile } = useAuth();
  const [sport, setSport] = useState('americanfootball_nfl');
  const [oddsFormat, setOddsFormat] = useState('american');
  const [darkMode, setDarkMode] = useState(() => {
    const stored = localStorage.getItem('ai-sports-edge-dark-mode');
    return stored !== null ? stored === 'true' : true;
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem('ai-sports-edge-sidebar') === 'collapsed';
  });

  // Apply saved preferences from profile when it loads
  useEffect(() => {
    if (!profile) return;
    if (profile.dark_mode !== undefined) {
      setDarkMode(profile.dark_mode);
      localStorage.setItem('ai-sports-edge-dark-mode', String(profile.dark_mode));
    }
    if (profile.odds_format) {
      setOddsFormat(profile.odds_format);
    }
  }, [profile]);

  useEffect(() => {
    localStorage.setItem('ai-sports-edge-dark-mode', String(darkMode));
  }, [darkMode]);

  // Default to user's preferred sport, or first active sport
  useEffect(() => {
    if (profile?.default_sport) {
      setSport(profile.default_sport);
      return;
    }
    import('./api/client').then(({ default: client }) => {
      client.get('/api/sports').then((r) => {
        const activeKeys = new Set(
          (r.data || []).filter((s) => s.active).map((s) => s.key)
        );
        const first = SPORT_KEYS.find((k) => activeKeys.has(k));
        if (first && first !== sport) setSport(first);
      }).catch(() => {});
    });
  }, [profile]);

  // Sync sidebar state from Sidebar component's localStorage writes
  useEffect(() => {
    function onStorage() {
      setSidebarCollapsed(localStorage.getItem('ai-sports-edge-sidebar') === 'collapsed');
      // Also sync dark mode from settings page save
      const dm = localStorage.getItem('ai-sports-edge-dark-mode');
      if (dm !== null) setDarkMode(dm === 'true');
    }
    window.addEventListener('storage', onStorage);
    const interval = setInterval(onStorage, 300);
    return () => { window.removeEventListener('storage', onStorage); clearInterval(interval); };
  }, []);

  const isGolf = sport === 'golf';
  const ml = sidebarCollapsed ? 'lg:ml-[68px]' : 'lg:ml-[180px]';

  return (
    <div className={`min-h-screen flex flex-col transition-colors duration-150 ${darkMode ? 'dark bg-[#0f1219] text-[#e2e8f0]' : 'bg-[#f8fafc] text-[#1e293b]'}`}>
      <Sidebar />
      <Navbar
        sport={sport}
        setSport={setSport}
        sidebarCollapsed={sidebarCollapsed}
        darkMode={darkMode}
        setDarkMode={setDarkMode}
        favoriteSports={profile?.favorite_sports || []}
      />
      <main className={`flex-1 pt-12 transition-all duration-200 ${ml}`}>
        <Routes>
          <Route
            path="/"
            element={
              isGolf
                ? <GolfBoard oddsFormat={oddsFormat} />
                : <OddsBoard sport={sport} oddsFormat={oddsFormat} favoriteTeams={profile?.favorite_teams || []} />
            }
          />
          <Route path="/game/:eventId" element={<GameDetail />} />
          <Route path="/alerts" element={<ValueAlerts />} />
          <Route path="/briefing" element={<DailyBriefing />} />
          <Route path="/patterns" element={<PatternFinder />} />
          <Route path="/learn" element={<LearnPage />} />
          <Route path="/parlay" element={<ParlayBuilder />} />
          <Route path="/bets" element={<BetTracker />} />
          <Route path="/predictions" element={<PredictionsPage />} />
          <Route path="/props" element={<PropPicks />} />
          <Route path="/admin/quota" element={<AdminGate><QuotaDashboard /></AdminGate>} />
          <Route path="/admin/accuracy" element={<AdminGate><AccuracyDashboard /></AdminGate>} />
          <Route path="/admin/controls" element={<AdminGate><ManualControls /></AdminGate>} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/auth" element={<AuthPage />} />
        </Routes>
      </main>
      <div className={`transition-all duration-200 ${ml}`}>
        <Footer />
      </div>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppShell />
      </AuthProvider>
    </BrowserRouter>
  );
}
